import { MegaminxTop } from "./../puzzles/megaminxTop";
import { RubiksCubeTopLayer } from "./../puzzles/rubiksCube/rubiksCubeTop";
import { Square1Net } from "./../puzzles/square1/square1Net";
import { Square1Simualtor } from "./../simulator/square1/square1Simulator";
import { Square1 } from "./../puzzles/square1/square1";
import { SkewbNet } from "./../puzzles/skewbNet";
import { SkewbSimulator } from "./../simulator/skewb/skewbSimulator";
import { Skewb } from "./../puzzles/skewb";
import { PyraminxSimulator } from "./../simulator/pyraminx/pyraminxSimulator";
import { PyraminxNet } from "./../puzzles/pyraminxNet";
import { MegaminxNet } from "./../puzzles/megaminxNet";
import { Megaminx } from "./../puzzles/megaminx";
import { MegaminxSimulator } from "./../simulator/megaminx/megaminxSimulator";
import { RubiksCubeNet } from "./../puzzles/rubiksCube/rubiksCubeNet";
import { RubiksCube } from "../puzzles/rubiksCube/rubiksCube";
import { RubiksCubeSimulator } from "../simulator/rubiksCube/rubiksCubeSimulator";
import { Pyraminx } from "../puzzles/pyraminx";
import { VisualizerType } from "./enum";
/**
 * Since puzzle geometry doesn't change for any instance of "Visuzlier"
 * we can cache the geometry generated to avoid generating it on each time
 * we render a puzzle
 */
const geometryCache = {
    [VisualizerType.CUBE]: {},
    [VisualizerType.CUBE_NET]: {},
    [VisualizerType.CUBE_TOP]: {},
    [VisualizerType.MEGAMINX]: {},
    [VisualizerType.MEGAMINX_NET]: {},
    [VisualizerType.MEGAMINX_TOP]: {},
    [VisualizerType.PYRAMINX]: {},
    [VisualizerType.PYRAMINX_NET]: {},
    [VisualizerType.SKEWB]: {},
    [VisualizerType.SKEWB_NET]: {},
    [VisualizerType.SQUARE1]: {},
    [VisualizerType.SQUARE1_NET]: {},
};
/**
 * Creates puzzle geometry for a given puzzle type.
 * Will initialize the geometry on puzzle options
 * passed in
 *
 * @param type Type of the puzzle {@link VisualizerType} (cube, skewb, etc...)
 * @param options  Puzzle options {@link PuzzleOptions}
 */
export function getPuzzleGeometry(type, options) {
    switch (type) {
        case VisualizerType.CUBE:
            return createCube(options);
        case VisualizerType.CUBE_NET:
            return createCubeNet(options);
        case VisualizerType.CUBE_TOP:
            return createCubeTop(options);
        case VisualizerType.MEGAMINX:
            return createMegaminx(options);
        case VisualizerType.MEGAMINX_NET:
            return createMegaminxNet(options);
        case VisualizerType.MEGAMINX_TOP:
            return createMegaminxTop(options);
        case VisualizerType.PYRAMINX:
            return createPyraminx(options);
        case VisualizerType.PYRAMINX_NET:
            return createPyraminxNet(options);
        case VisualizerType.SKEWB:
            return createSkewb(options);
        case VisualizerType.SKEWB_NET:
            return createSkewbNet(options);
        case VisualizerType.SQUARE1:
            return createSquare1(options);
        case VisualizerType.SQUARE1_NET:
            return createSquare1Net(options);
    }
}
/**
 * Returns a puzzle simulator for the type of puzzle
 *
 * @param type
 * @param options
 */
export function getPuzzleSimulator(type, options) {
    switch (type) {
        case VisualizerType.CUBE:
        case VisualizerType.CUBE_NET:
        case VisualizerType.CUBE_TOP:
            return new RubiksCubeSimulator(options.size);
        case VisualizerType.MEGAMINX:
        case VisualizerType.MEGAMINX_NET:
        case VisualizerType.MEGAMINX_TOP:
            return new MegaminxSimulator();
        case VisualizerType.PYRAMINX:
        case VisualizerType.PYRAMINX_NET:
            return new PyraminxSimulator();
        case VisualizerType.SKEWB:
        case VisualizerType.SKEWB_NET:
            return new SkewbSimulator();
        case VisualizerType.SQUARE1:
        case VisualizerType.SQUARE1_NET:
            return initSquare1Simulator(options);
    }
}
export function createCube(options = {}) {
    if (!geometryCache[VisualizerType.CUBE][options.size]) {
        geometryCache[VisualizerType.CUBE][options.size] = new RubiksCube(options.size);
    }
    return geometryCache[VisualizerType.CUBE][options.size];
}
export function createCubeNet(options = {}) {
    if (!geometryCache[VisualizerType.CUBE_NET][options.size]) {
        geometryCache[VisualizerType.CUBE_NET][options.size] = new RubiksCubeNet(options.size);
    }
    return geometryCache[VisualizerType.CUBE_NET][options.size];
}
export function createCubeTop(options = {}) {
    if (!geometryCache[VisualizerType.CUBE_TOP][options.size]) {
        geometryCache[VisualizerType.CUBE_TOP][options.size] =
            new RubiksCubeTopLayer(options.size);
    }
    return geometryCache[VisualizerType.CUBE_TOP][options.size];
}
export function createMegaminx(options = {}) {
    if (!geometryCache[VisualizerType.MEGAMINX][options.size]) {
        geometryCache[VisualizerType.MEGAMINX][options.size] = new Megaminx(options.size);
    }
    return geometryCache[VisualizerType.MEGAMINX][options.size];
}
export function createMegaminxNet(options = {}) {
    if (!geometryCache[VisualizerType.MEGAMINX_NET][options.size]) {
        geometryCache[VisualizerType.MEGAMINX_NET][options.size] = new MegaminxNet(options.size);
    }
    return geometryCache[VisualizerType.MEGAMINX_NET][options.size];
}
export function createMegaminxTop(options = {}) {
    if (!geometryCache[VisualizerType.MEGAMINX_TOP][2]) {
        // megaminx top size not supported, so just cache by size 2
        geometryCache[VisualizerType.MEGAMINX_TOP][2] = new MegaminxTop();
    }
    return geometryCache[VisualizerType.MEGAMINX_TOP][2];
}
export function createPyraminx(options = {}) {
    if (!geometryCache[VisualizerType.PYRAMINX][options.size]) {
        geometryCache[VisualizerType.PYRAMINX][options.size] = new Pyraminx(options.size);
    }
    return geometryCache[VisualizerType.PYRAMINX][options.size];
}
export function createPyraminxNet(options = {}) {
    if (!geometryCache[VisualizerType.PYRAMINX_NET][options.size]) {
        geometryCache[VisualizerType.PYRAMINX_NET][options.size] = new PyraminxNet(options.size);
    }
    return geometryCache[VisualizerType.PYRAMINX_NET][options.size];
}
export function createSkewb(options = {}) {
    if (!geometryCache[VisualizerType.SKEWB][1]) {
        // Skewb size not supported, so just cache by size 1
        geometryCache[VisualizerType.SKEWB][1] = new Skewb();
    }
    return geometryCache[VisualizerType.SKEWB][1];
}
export function createSkewbNet(options = {}) {
    if (!geometryCache[VisualizerType.SKEWB_NET][1]) {
        // Skewb size not supported, so just cache by size 1
        geometryCache[VisualizerType.SKEWB_NET][1] = new SkewbNet();
    }
    return geometryCache[VisualizerType.SKEWB_NET][1];
}
export function createSquare1(options = {}) {
    const simulator = initSquare1Simulator(options);
    const geometry = new Square1(simulator.topLayer, simulator.bottomLayer, simulator.middleRotated, options.scheme);
    return geometry;
}
export function createSquare1Net(options = {}) {
    const simulator = initSquare1Simulator(options);
    const geometry = new Square1Net(simulator.topLayer, simulator.bottomLayer, simulator.middleRotated, options.scheme);
    return geometry;
}
function initSquare1Simulator(options) {
    const simulator = new Square1Simualtor(options.scheme);
    if (options.case) {
        simulator.case(options.case);
    }
    else if (options.alg) {
        simulator.alg(options.alg);
    }
    return simulator;
}
